import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Sidebar from '../../components/sidebar/Sidebar';
import lpGenerationService, {
  LpProject,
  LpSection,
  CreateProjectData,
  UpdateSectionData,
  UpdateSectionOverlayData,
  LpGenerationMode,
} from '../../services/api/lpGenerationService';
import GenerationModeSelector from '../../components/lp/GenerationModeSelector';
import OverlaySectionEditor from '../../components/lp/OverlaySectionEditor';
import StyleProfileCard from '../../components/lp/StyleProfileCard';
import SmartImageEditModal from '../../components/image-generation/SmartImageEditModal';
import type { GeneratedImage } from '../../services/api/imageGenerationService';
import {
  ProductSelectionModal,
  ProductSelectionData,
  ProductImageSelectionModal,
  SelectedProductImage,
  LpStyleSelectModal,
  SelectedLpStyle,
} from '../../components/image-generation';
import { selectOrganization } from '../../redux/organization/organizationSlice';
import { ResponsiveAppShell, useConfirm, TaskyPageContainer } from '../../components/tasky';
import { TaskyHeaderActions } from '../../components/tasky/layout/TaskyHeaderActions';
import { useSetAdmanPageContext } from '../../components/adman/useAdmanPageContext';
import { useAdmanRefreshHandler } from '../../components/adman/useAdmanRefreshHandler';
import {
  FileText, Menu, ChevronDown, ChevronRight, Download, Trash2, Loader2,
  Eye, Edit3, Plus, RefreshCw, Settings, X, Upload, FolderOpen,
  Sparkles, AlertTriangle, Lightbulb, Star, MessageSquare, MousePointer2,
  HelpCircle, Layout, Image as ImageIcon,
} from 'lucide-react';

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

const STATUS_LABELS: Record<LpProject['status'], string> = {
  draft: '下書き',
  wireframe_generating: 'WF生成中',
  wireframe_done: 'WF完了',
  design_generating: 'デザイン生成中',
  design_done: 'デザイン完了',
  completed: '完了',
  failed: 'エラー',
};

const SECTION_TYPE_LABELS: Record<string, string> = {
  hero: 'ヒーロー',
  problem: '問題提起',
  solution: '解決策',
  features: '特徴・機能',
  benefits: 'メリット',
  testimonials: 'お客様の声',
  cta: 'CTA（行動喚起）',
  faq: 'よくある質問',
  footer: 'フッター',
};

const SECTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  hero: Sparkles,
  problem: AlertTriangle,
  solution: Lightbulb,
  features: Star,
  benefits: Star,
  testimonials: MessageSquare,
  cta: MousePointer2,
  faq: HelpCircle,
  footer: Layout,
};

const getSectionIcon = (type: string) => {
  return SECTION_ICONS[type] || FileText;
};

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------

const LpCreationPage: React.FC = () => {
  const organization = useSelector(selectOrganization);
  const confirm = useConfirm();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Data ---
  const [projects, setProjects] = useState<LpProject[]>([]);
  const [currentProject, setCurrentProject] = useState<LpProject | null>(null);

  // --- Step 1 form ---
  const [productInfo, setProductInfo] = useState('');
  const [selectedStyleId, setSelectedStyleId] = useState<number | undefined>(undefined);
  const [selectedStyleName, setSelectedStyleName] = useState<string>('');
  const [generationMode, setGenerationMode] = useState<LpGenerationMode>('decorative');
  const [selectedProductData, setSelectedProductData] = useState<ProductSelectionData | null>(null);
  // 広告自動化マンに「今この商品を見ている」と伝える（F20-a）。既存ロジックには触れない。
  useSetAdmanPageContext(
    selectedProductData ? { product_ids: [selectedProductData.product.id] } : null
  );
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [showStyleSelectModal, setShowStyleSelectModal] = useState(false);
  const [showImageSelectionModal, setShowImageSelectionModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // --- Reference images (matching banner page pattern) ---
  const [referenceImages, setReferenceImages] = useState<SelectedProductImage[]>([]);
  const [uploadedReferenceImages, setUploadedReferenceImages] = useState<Array<{
    id: string;
    file?: File;
    preview: string;
    base64: string;
    category: 'brand_logo' | 'product_image' | 'material';
  }>>([]);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // --- Step 2 WF editor ---
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});
  const [sectionEdits, setSectionEdits] = useState<Record<number, Partial<LpSection>>>({});
  const [overlaySectionEdits, setOverlaySectionEdits] = useState<Record<number, UpdateSectionOverlayData>>({});
  const [savingSection, setSavingSection] = useState<number | null>(null);
  const [sectionImageEdit, setSectionImageEdit] = useState<{ sectionIndex: number; image: GeneratedImage } | null>(null);
  const [registeringImageSection, setRegisteringImageSection] = useState<number | null>(null);

  // --- Step 3 preview ---
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // --- Polling ---
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ------------------------------------------------------------------
  // Initial data loading
  // ------------------------------------------------------------------

  useEffect(() => {
    loadProjects();
    return () => stopPolling();
  }, []);

  const loadProjects = async () => {
    try {
      const result = await lpGenerationService.getProjects();
      if (result.success) {
        setProjects(result.data.filter(project => !project.workflow));
      }
    } catch (err) {
      console.error('Failed to load LP projects:', err);
    }
  };

  // 広告自動化マンの「更新する」ボタンから呼べるようにする（F23-c）。
  useAdmanRefreshHandler(loadProjects);

  // Deep-link: auto-select project when ?project=<uuid> query param is present
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkProcessedRef = useRef(false);
  useEffect(() => {
    if (deepLinkProcessedRef.current) return;
    const projectId = searchParams.get('project');
    if (!projectId) return;
    deepLinkProcessedRef.current = true;
    (async () => {
      try {
        const result = await lpGenerationService.getGenerationStatus(projectId);
        if (result.success && result.data) {
          setCurrentProject(result.data);
          if (result.data.status === 'design_done' || result.data.status === 'completed') {
            loadPreview(projectId);
          }
        }
        const next = new URLSearchParams(searchParams);
        next.delete('project');
        setSearchParams(next, { replace: true });
      } catch (err) {
        console.error('Deep-link LP project load failed:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ------------------------------------------------------------------
  // Polling for generation status
  // ------------------------------------------------------------------

  const startPolling = useCallback((projectId: string) => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const result = await lpGenerationService.getGenerationStatus(projectId);
        if (result.success) {
          setCurrentProject(result.data);
          const status = result.data.status;
          if (
            status === 'wireframe_done' ||
            status === 'design_done' ||
            status === 'completed' ||
            status === 'failed'
          ) {
            stopPolling();
            loadProjects();
            if (status === 'design_done' || status === 'completed') {
              loadPreview(projectId);
            }
          }
        }
      } catch {
        // keep polling
      }
    }, 5000);
  }, []);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  // ------------------------------------------------------------------
  // Step 1: Create project
  // ------------------------------------------------------------------

  const handleCreateProject = async () => {
    if (!productInfo.trim()) {
      setError('商品情報を入力してください。');
      return;
    }
    setError(null);
    setIsCreating(true);

    try {
      // Build reference_images metadata
      const refImagesMetadata: any[] = [];
      referenceImages.forEach((img) => {
        refImagesMetadata.push({ type: img.category, s3_key: img.s3Key, url: img.url });
      });
      uploadedReferenceImages.forEach((img) => {
        refImagesMetadata.push({ type: img.category, image_data: img.base64 });
      });

      const data: CreateProjectData = {
        product_info: productInfo,
        lp_style_id: selectedStyleId,
        generation_mode: generationMode,
        product_data: selectedProductData
          ? { id: selectedProductData.product?.id, name: selectedProductData.product?.name }
          : undefined,
        reference_images: refImagesMetadata.length > 0 ? refImagesMetadata : undefined,
      };

      const result = await lpGenerationService.createProject(data);
      if (result.success && result.data) {
        setCurrentProject(result.data);
        startPolling(result.data.id);
        // Reset form
        setProductInfo('');
        setSelectedStyleId(undefined);
        setSelectedStyleName('');
        setGenerationMode('decorative');
        setSelectedProductData(null);
        setReferenceImages([]);
        uploadedReferenceImages.forEach(img => URL.revokeObjectURL(img.preview));
        setUploadedReferenceImages([]);
      } else {
        setError(result.error || 'プロジェクトの作成に失敗しました。');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'プロジェクトの作成中にエラーが発生しました。');
    } finally {
      setIsCreating(false);
    }
  };

  // ------------------------------------------------------------------
  // Reference image handlers (matching banner page pattern)
  // ------------------------------------------------------------------

  const handleReferenceImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: typeof uploadedReferenceImages = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/') && file.type !== 'image/svg+xml') continue;
      if (file.size > 10 * 1024 * 1024) {
        setError(`${file.name} は10MBを超えています。`);
        continue;
      }

      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.readAsDataURL(file);
      });

      newImages.push({
        id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        preview: URL.createObjectURL(file),
        base64,
        category: 'material',
      });
    }
    setUploadedReferenceImages(prev => [...prev, ...newImages]);
    if (e.target) e.target.value = '';
  };

  const handleRemoveUploadedImage = (id: string) => {
    setUploadedReferenceImages(prev => {
      const img = prev.find(i => i.id === id);
      if (img) URL.revokeObjectURL(img.preview);
      return prev.filter(i => i.id !== id);
    });
  };

  // ------------------------------------------------------------------
  // Product selection callback
  // ------------------------------------------------------------------

  const handleProductSelected = (data: ProductSelectionData) => {
    setSelectedProductData(data);
    const product = data.product;
    if (product) {
      const parts = [product.name];
      if (product.description) parts.push(product.description);
      if (product.extended_metadata?.product?.usp) parts.push(`USP: ${product.extended_metadata.product.usp}`);
      setProductInfo(parts.join('\n'));
    }

    // Convert selected product images to reference images
    if (data.selectedImages && data.selectedImages.length > 0) {
      const convertedImages: SelectedProductImage[] = data.selectedImages.map(img => ({
        id: img.id,
        url: img.url,
        s3Key: img.file_path,
        category: img.category as any,
        productId: img.product_id,
        productName: data.product.name,
        fileName: img.file_name,
      }));
      setReferenceImages(convertedImages);
    }

    setShowProductSelectionModal(false);
  };

  // ------------------------------------------------------------------
  // Style selection
  // ------------------------------------------------------------------

  const handleStyleSelect = (style: SelectedLpStyle | null) => {
    if (style) {
      setSelectedStyleId(style.id);
      setSelectedStyleName(style.name);
    } else {
      setSelectedStyleId(undefined);
      setSelectedStyleName('');
    }
    setShowStyleSelectModal(false);
  };

  // ------------------------------------------------------------------
  // Step 2: Edit sections
  // ------------------------------------------------------------------

  const toggleSectionExpand = (index: number) => {
    setExpandedSections(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleSectionFieldChange = (index: number, field: keyof LpSection, value: string) => {
    setSectionEdits(prev => ({
      ...prev,
      [index]: { ...prev[index], [field]: value },
    }));
  };

  const handleOverlaySectionChange = (index: number, data: UpdateSectionOverlayData) => {
    setOverlaySectionEdits(prev => ({
      ...prev,
      [index]: { ...prev[index], ...data },
    }));
  };

  const handleSaveSection = async (index: number) => {
    if (!currentProject) return;
    const edits = sectionEdits[index];
    const overlayEdits = overlaySectionEdits[index];
    if (!edits && !overlayEdits) return;

    setSavingSection(index);
    try {
      let result = { success: true, data: currentProject };
      if (edits) {
        const updateData: UpdateSectionData = {};
        if (edits.title !== undefined) updateData.title = edits.title;
        if (edits.content !== undefined) updateData.content = edits.content;
        if (edits.image_url !== undefined) updateData.image_url = edits.image_url;

        result = await lpGenerationService.updateSection(currentProject.id, index, updateData);
      }

      if (result.success && overlayEdits) {
        result = await lpGenerationService.updateSectionOverlay(currentProject.id, index, overlayEdits);
      }

      if (result.success) {
        setCurrentProject(result.data);
        setSectionEdits(prev => {
          const next = { ...prev };
          if (next[index]) {
            const { image_url, ...remainingEdits } = next[index];
            if (Object.keys(remainingEdits).length > 0) {
              next[index] = remainingEdits;
            } else {
              delete next[index];
            }
          }
          return next;
        });
        setOverlaySectionEdits(prev => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'セクションの保存に失敗しました。');
    } finally {
      setSavingSection(null);
    }
  };

  const handleStartSectionImageEdit = async (index: number) => {
    if (!currentProject) return;

    setError(null);
    setRegisteringImageSection(index);
    try {
      const result = await lpGenerationService.registerSectionImage(currentProject.id, index);
      if (result.success && result.data) {
        setSectionImageEdit({ sectionIndex: index, image: result.data });
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || '画像編集の準備に失敗しました。');
    } finally {
      setRegisteringImageSection(null);
    }
  };

  const handleApplySectionImageEdit = async (editedImage: GeneratedImage) => {
    if (!currentProject || !sectionImageEdit) return;

    const index = sectionImageEdit.sectionIndex;
    try {
      const result = await lpGenerationService.updateSection(currentProject.id, index, {
        image_url: editedImage.url,
      });

      if (result.success && result.data) {
        setCurrentProject(result.data);
        setSectionEdits(prev => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
        setSectionImageEdit(null);
        if (result.data.status === 'design_done' || result.data.status === 'completed') {
          await loadPreview(result.data.id);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || '編集済み画像の反映に失敗しました。');
    }
  };

  const handleGenerateDesign = async () => {
    if (!currentProject) return;
    setError(null);

    try {
      const result = await lpGenerationService.generateDesign(currentProject.id);
      if (result.success && result.data) {
        setCurrentProject(result.data);
        startPolling(result.data.id);
      } else {
        setError(result.error || 'デザイン生成の開始に失敗しました。');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'デザイン生成中にエラーが発生しました。');
    }
  };

  // ------------------------------------------------------------------
  // Step 3: Preview
  // ------------------------------------------------------------------

  const loadPreview = async (projectId: string) => {
    try {
      const result = await lpGenerationService.getPreview(projectId);
      if (result.success && result.data) {
        const { html, css, js } = result.data;
        if (html && html.includes('<!DOCTYPE')) {
          setPreviewHtml(html);
        } else {
          const fullHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<style>${css || ''}</style></head>
<body>${html || ''}<script>${js || ''}</script></body></html>`;
          setPreviewHtml(fullHtml);
        }
      }
    } catch (err) {
      console.error('Failed to load preview:', err);
    }
  };

  const handleDownloadHtml = () => {
    if (!currentProject) return;
    const { design_html, design_css, design_js } = currentProject;
    let fullHtml: string;
    if (design_html && design_html.includes('<!DOCTYPE')) {
      fullHtml = design_html;
    } else {
      fullHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<style>${design_css || ''}</style></head>
<body>${design_html || ''}<script>${design_js || ''}</script></body></html>`;
    }

    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lp-${currentProject.id}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ------------------------------------------------------------------
  // Project history
  // ------------------------------------------------------------------

  const handleLoadProject = async (project: LpProject) => {
    setError(null);
    setCurrentProject(project);

    if (project.status === 'wireframe_generating' || project.status === 'design_generating') {
      startPolling(project.id);
    } else if (project.status === 'design_done' || project.status === 'completed') {
      loadPreview(project.id);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    const confirmed = await confirm({
      title: 'このプロジェクトを削除してもよろしいですか？',
      tone: 'destructive',
      confirmText: '削除する',
    });
    if (!confirmed) return;

    try {
      await lpGenerationService.deleteProject(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      if (currentProject?.id === projectId) {
        setCurrentProject(null);
        setPreviewHtml('');
        stopPolling();
      }
    } catch (err) {
      setError('プロジェクトの削除に失敗しました。');
    }
  };

  const handleNewProject = () => {
    setCurrentProject(null);
    setPreviewHtml('');
    setSectionEdits({});
    setOverlaySectionEdits({});
    setExpandedSections({});
    stopPolling();
  };

  // ------------------------------------------------------------------
  // Determine which step to show
  // ------------------------------------------------------------------

  const getStepView = (): 'form' | 'generating' | 'wireframe' | 'design_preview' => {
    if (!currentProject) return 'form';
    const s = currentProject.status;
    if (s === 'draft') return 'form';
    if (s === 'wireframe_generating' || s === 'design_generating') return 'generating';
    if (s === 'wireframe_done') return 'wireframe';
    if (s === 'design_done' || s === 'completed') return 'design_preview';
    if (s === 'failed') return currentProject.sections ? 'wireframe' : 'form';
    return 'form';
  };

  const stepView = getStepView();
  const totalRefImages = referenceImages.length + uploadedReferenceImages.length;
  const creativeConcept = currentProject?.product_data?.creative_concept;
  const layoutImageUrl = currentProject?.product_data?.layout_image_url;

  // ------------------------------------------------------------------
  // Section editor component (reused in wireframe & preview)
  // ------------------------------------------------------------------

  const renderSectionEditor = (section: LpSection, index: number, compact = false) => {
    const isExpanded = expandedSections[index] ?? !compact;
    const edits = sectionEdits[index] || {};
    const title = edits.title ?? section.title;
    const content = edits.content ?? section.content;
    const imageUrl = edits.image_url ?? section.image_url ?? '';
    const overlayEdits = overlaySectionEdits[index];
    const hasChanges = Object.keys(edits).length > 0 || !!overlayEdits;
    const isSaving = savingSection === index;
    const SectionIcon = getSectionIcon(section.type);
    const isOverlayMode = currentProject?.generation_mode === 'image2_overlay';

    return (
      <div key={index} className="border border-warm-border rounded-lg overflow-hidden mb-2">
        <button
          onClick={() => toggleSectionExpand(index)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 bg-primary/10 rounded">
              <SectionIcon className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-medium">
              {SECTION_TYPE_LABELS[section.type] || section.type}
            </span>
            <span className="text-sm font-medium text-gray-900">{section.title}</span>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {isExpanded && (
          <div className="px-4 py-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">タイトル</label>
              <input
                type="text"
                value={title}
                onChange={e => handleSectionFieldChange(index, 'title', e.target.value)}
                className="w-full px-3 py-2 border border-warm-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">コンテンツ</label>
              <textarea
                value={content}
                onChange={e => handleSectionFieldChange(index, 'content', e.target.value)}
                className={`w-full px-3 py-2 border border-warm-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y ${compact ? 'h-20' : 'h-24'}`}
              />
            </div>
            {section.image_url && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">セクション画像</label>
                <div className="flex items-start gap-3">
                  <img
                    src={section.image_url}
                    alt={section.title}
                    className="w-24 h-24 object-cover rounded-lg border border-warm-border"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <input
                    type="text"
                    value={imageUrl}
                    onChange={e => handleSectionFieldChange(index, 'image_url', e.target.value)}
                    placeholder="https://..."
                    className="flex-1 px-3 py-2 border border-warm-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            )}
            {!section.image_url && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">画像URL（任意）</label>
                <input
                  type="text"
                  value={imageUrl}
                  onChange={e => handleSectionFieldChange(index, 'image_url', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-warm-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
            {isOverlayMode && (
              <OverlaySectionEditor
                section={{
                  ...section,
                  cta_overlay: overlayEdits?.cta_overlay ?? section.cta_overlay,
                  sr_only_text: overlayEdits?.sr_only_text ?? section.sr_only_text,
                }}
                sectionIndex={index}
                onChange={(data) => handleOverlaySectionChange(index, data)}
                onImageEdit={registeringImageSection === index ? undefined : handleStartSectionImageEdit}
              />
            )}
            {hasChanges && (
              <div className="flex justify-end">
                <button
                  onClick={() => handleSaveSection(index)}
                  disabled={isSaving}
                  className="px-4 py-1.5 bg-primary text-white text-sm rounded-md hover:bg-primary-hover disabled:opacity-50 flex items-center gap-1"
                >
                  {isSaving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Edit3 className="w-3.5 h-3.5" />
                  )}
                  保存
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderConceptPanel = () => {
    if (!creativeConcept && !layoutImageUrl) return null;

    return (
      <div className="border border-warm-border rounded-lg p-4 bg-white">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-gray-900">生成コンセプト</h3>
            </div>
            {creativeConcept && (
              <div className="space-y-2">
                <p className="text-base font-semibold text-gray-900">
                  {creativeConcept.name || 'One Story Canvas'}
                </p>
                {creativeConcept.tagline && (
                  <p className="text-sm text-gray-700">{creativeConcept.tagline}</p>
                )}
                {creativeConcept.layout_principle && (
                  <p className="text-xs leading-5 text-gray-500">{creativeConcept.layout_principle}</p>
                )}
              </div>
            )}
          </div>
          {!layoutImageUrl && (
            <div className="shrink-0 w-10 h-10 rounded-md bg-primary/5 flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-primary" />
            </div>
          )}
        </div>
        {layoutImageUrl && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-gray-600">LP全体構成画像</p>
              <a
                href={layoutImageUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary hover:text-primary-hover"
              >
                別タブで開く
              </a>
            </div>
            <a
              href={layoutImageUrl}
              target="_blank"
              rel="noreferrer"
              className="block border border-warm-border rounded-md overflow-hidden bg-gray-50 hover:opacity-95"
              title="全体構成画像を開く"
            >
              <img
                src={layoutImageUrl}
                alt="LP全体構成"
                className="w-full max-h-[560px] object-contain bg-gray-50"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </a>
          </div>
        )}
      </div>
    );
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle="LP制作"
      topbarActions={
        <>
          {currentProject && (
            <button
              onClick={handleNewProject}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-primary hover:bg-primary/5"
              aria-label="新規プロジェクト"
              type="button"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </>
      }
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-white lg:h-screen lg:min-h-0">
        {/* Header */}
        <div className="hidden h-14 shrink-0 items-center justify-between border-b border-warm-border bg-white px-4 lg:flex">
          <div className="flex items-center space-x-3">
            {sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(false)}
                className="p-1 h-8 w-8 hover:bg-gray-100 rounded-md"
                title="サイドバーを展開"
              >
                <Menu className="w-4 h-4" />
              </button>
            )}
            <FileText className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-semibold text-gray-900">LP制作</h1>
            {currentProject && (
              <button
                onClick={handleNewProject}
                className="ml-4 flex items-center gap-1 text-sm text-primary hover:text-primary-hover"
              >
                <Plus className="w-4 h-4" />
                新規プロジェクト
              </button>
            )}
          </div>
          {/* 固定ピルをページ内ヘッダに取り込む（/ads/lp は App 側の固定ピルを出さない）。 */}
          <TaskyHeaderActions />
        </div>

        {/* Main Scrollable Content */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer>
            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Failed status message */}
            {currentProject?.status === 'failed' && currentProject.error_message && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  エラーが発生しました: {currentProject.error_message}
                </p>
              </div>
            )}

            {/* ========== Step 1: Input Form ========== */}
            {stepView === 'form' && (
              <div className="space-y-6">
                <div className="bg-white border border-warm-border rounded-lg p-6">
                  <h2 className="text-base font-semibold text-gray-900 mb-4">商品情報の入力</h2>

                  {/* Product selection */}
                  <div className="mb-4">
                    <button
                      onClick={() => setShowProductSelectionModal(true)}
                      className="text-sm text-primary hover:text-primary-hover underline"
                    >
                      商品マスタから選択する
                    </button>
                    {selectedProductData && (
                      <div className="mt-2 flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                        {/* Show product images if available */}
                        {selectedProductData.selectedImages && selectedProductData.selectedImages.length > 0 && (
                          <div className="flex gap-1.5">
                            {selectedProductData.selectedImages.slice(0, 3).map((img) => (
                              <img
                                key={img.id}
                                src={img.url}
                                alt={img.file_name}
                                className="w-12 h-12 object-contain bg-white rounded border border-warm-border"
                              />
                            ))}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {selectedProductData.product?.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {selectedProductData.segmentName && `${selectedProductData.segmentName}`}
                            {selectedProductData.appealName && ` / ${selectedProductData.appealName}`}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedProductData(null);
                            setReferenceImages([]);
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Product info textarea */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      商品・サービス情報 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={productInfo}
                      onChange={e => setProductInfo(e.target.value)}
                      placeholder="商品名、特徴、ターゲット、USPなどを入力してください..."
                      className="w-full h-32 px-3 py-2 border border-warm-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
                    />
                  </div>

                  <div className="mb-4">
                    <GenerationModeSelector value={generationMode} onChange={setGenerationMode} />
                  </div>

                  {/* Style selection - Modal pattern (matching banner page) */}
                  <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        LPスタイル（任意）
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setShowStyleSelectModal(true)}
                          className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md hover:bg-gray-50 transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                          スタイルを選ぶ
                        </button>
                        {selectedStyleId && selectedStyleName && (
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/20 rounded-md">
                            <span className="text-sm text-primary font-medium">{selectedStyleName}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedStyleId(undefined);
                                setSelectedStyleName('');
                              }}
                              className="text-primary hover:text-primary-hover"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                  {/* Reference Images Section (matching banner page) */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">参考画像（任意）</label>
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setShowImageSelectionModal(true)}
                        className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <FolderOpen className="w-4 h-4" />
                        商品画像から選択
                      </button>
                      <button
                        type="button"
                        onClick={() => uploadInputRef.current?.click()}
                        className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        アップロード
                      </button>
                      <input
                        ref={uploadInputRef}
                        type="file"
                        accept="image/*,.svg,image/svg+xml"
                        multiple
                        onChange={handleReferenceImageUpload}
                        className="hidden"
                      />
                      {totalRefImages > 0 && (
                        <span className="text-sm text-gray-600">
                          {totalRefImages}枚選択中
                        </span>
                      )}
                    </div>

                    {/* Reference Images Preview */}
                    {totalRefImages > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {referenceImages.map((img) => (
                          <div key={img.id} className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-purple">
                            <img src={img.url} alt={img.fileName || '商品画像'} className="w-full h-full object-contain bg-gray-100" />
                            <button
                              type="button"
                              onClick={() => setReferenceImages(prev => prev.filter(i => i.id !== img.id))}
                              className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 truncate">
                              {img.category === 'brand_logo' ? 'ロゴ' : img.category === 'product_image' ? '商品' : '素材'}
                            </div>
                          </div>
                        ))}
                        {uploadedReferenceImages.map((img) => (
                          <div key={img.id} className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-green-500">
                            <img src={img.preview} alt="アップロード画像" className="w-full h-full object-contain bg-gray-100" />
                            <button
                              type="button"
                              onClick={() => handleRemoveUploadedImage(img.id)}
                              className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 truncate">
                              素材
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    onClick={handleCreateProject}
                    disabled={isCreating || !productInfo.trim()}
                    className="w-full sm:w-auto px-6 py-2.5 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
                    WF作成開始
                  </button>
                </div>
              </div>
            )}

            {/* ========== Generating ========== */}
            {stepView === 'generating' && currentProject && (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
                <p className="text-base font-medium text-gray-900 mb-1">
                  {currentProject.status === 'wireframe_generating'
                    ? 'ワイヤーフレームを生成しています...'
                    : 'デザインを生成しています...'}
                </p>
                <p className="text-sm text-gray-500">
                  {currentProject.status === 'design_generating'
                    ? 'セクション画像の生成とHTMLデザインの作成を行っています。数分かかる場合があります。'
                    : '数分かかる場合があります。このページを離れても処理は継続されます。'}
                </p>
              </div>
            )}

            {/* ========== Step 2: Wireframe Editor ========== */}
            {stepView === 'wireframe' && currentProject?.sections && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-base font-semibold text-gray-900">ワイヤーフレーム編集</h2>
                  <button
                    onClick={handleGenerateDesign}
                    className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary-hover flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    デザイン生成
                  </button>
                </div>

                {renderConceptPanel()}

                {currentProject.generation_mode === 'image2_overlay' && (
                  <StyleProfileCard profile={currentProject.style_profile} />
                )}

                {currentProject.sections.map((section, index) =>
                  renderSectionEditor(section, index, false)
                )}
              </div>
            )}

            {/* ========== Step 3: Design Preview ========== */}
            {stepView === 'design_preview' && currentProject && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-base font-semibold text-gray-900">LP プレビュー</h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => loadPreview(currentProject.id)}
                      className="px-3 py-2 border border-warm-border text-gray-700 text-sm rounded-md hover:bg-gray-50 flex items-center gap-1"
                    >
                      <RefreshCw className="w-4 h-4" />
                      更新
                    </button>
                    <button
                      onClick={handleDownloadHtml}
                      className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary-hover flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      HTMLダウンロード
                    </button>
                  </div>
                </div>

                {renderConceptPanel()}

                {currentProject.generation_mode === 'image2_overlay' && (
                  <StyleProfileCard profile={currentProject.style_profile} />
                )}

                {/* iframe preview */}
                <div
                  className={`border border-warm-border rounded-lg overflow-hidden bg-white ${
                    currentProject.generation_mode === 'image2_overlay' ? 'mx-auto max-w-[430px]' : ''
                  }`}
                  style={{ height: '70vh' }}
                >
                  {previewHtml ? (
                    <iframe
                      ref={iframeRef}
                      srcDoc={previewHtml}
                      title="LP Preview"
                      className="w-full h-full border-0"
                      sandbox="allow-scripts allow-same-origin"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      <Loader2 className="w-6 h-6 animate-spin mr-2" />
                      プレビューを読み込み中...
                    </div>
                  )}
                </div>

                {/* Section adjustment panel */}
                {currentProject.sections && currentProject.sections.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">セクション微調整</h3>
                    {currentProject.sections.map((section, index) =>
                      renderSectionEditor(section, index, true)
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ========== Project History ========== */}
            {projects.length > 0 && (
              <div className="mt-10 border-t border-warm-border pt-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">プロジェクト履歴</h2>
                <div className="space-y-2">
                  {projects.map(project => (
                    <div
                      key={project.id}
                      className={`flex items-center justify-between border rounded-lg px-4 py-3 cursor-pointer transition-colors ${
                        currentProject?.id === project.id
                          ? 'border-primary/40 bg-primary/5'
                          : 'border-warm-border hover:bg-gray-50'
                      }`}
                      onClick={() => handleLoadProject(project)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {project.title || project.product_info?.slice(0, 50) || 'Untitled'}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span
                            className={`text-xs px-2 py-0.5 rounded font-medium ${
                              project.status === 'completed' || project.status === 'design_done'
                                ? 'bg-green-100 text-green-700'
                                : project.status === 'failed'
                                ? 'bg-red-100 text-red-700'
                                : project.status.includes('generating')
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {STATUS_LABELS[project.status]}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(project.created_at).toLocaleDateString('ja-JP')}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleDeleteProject(project.id);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md ml-2"
                        title="削除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TaskyPageContainer>
        </div>
      </div>

      {/* Product Selection Modal */}
      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelected}
        />
      )}

      {/* Product Image Selection Modal (for reference images) */}
      {showImageSelectionModal && (
        <ProductImageSelectionModal
          isOpen={showImageSelectionModal}
          onClose={() => setShowImageSelectionModal(false)}
          onSelect={(selected) => { setReferenceImages(selected); setShowImageSelectionModal(false); }}
          maxSelection={5}
        />
      )}

      {/* Style Selection Modal */}
      <LpStyleSelectModal
        isOpen={showStyleSelectModal}
        onClose={() => setShowStyleSelectModal(false)}
        onSelect={handleStyleSelect}
        currentStyleId={selectedStyleId ?? null}
      />

      {sectionImageEdit && (
        <SmartImageEditModal
          image={sectionImageEdit.image}
          isOpen={true}
          onClose={() => setSectionImageEdit(null)}
          onApply={handleApplySectionImageEdit}
        />
      )}
    </ResponsiveAppShell>
  );
};

export default LpCreationPage;
